import { AfterContentInit, Component, EventEmitter, Input, Output, ViewChild } from '@angular/core'
import { FormControl, FormGroup, FormArray, NgForm } from '@angular/forms'
import { debounceTime } from 'rxjs/operators'
import { Brand, AmenityType, SearchCriteriaBrandsGQL, PropertyStyle } from '../../../../../graphql'
import { UnsubscribeOnDestroy, capitalize } from '../../../../utility'
import { defaultMoreFilters, MoreFilters } from '../more-filters'
import amenitiesData from './amenities.json';

@Component({
    selector: 'app-search-filters-extended',
    templateUrl: 'search-filters-extended.html'
})
export class SearchFiltersExtendedComponent extends UnsubscribeOnDestroy implements AfterContentInit {

    @Input() moreFilters: MoreFilters = defaultMoreFilters()
    @Input() listingCount: number 

    // moreFilters = defaultMoreFilters()
    formGroup: FormGroup
    amenitiesData = amenitiesData
    brands: Array<Brand>
    brandIds: string[]
    keyAmenityIds: string[]
    otherAmenities: Array<AmenityType>
    otherAmenityIds: string[]
    selectedBrandIds: string[] = []
    selectedBrandIdsStored: string [] = []
    allBrandIdsActive = true
    selectedKeyAmenityIds: string[] = []
    selectedOtherAmenityIds: string[] = []
    selectedAmenityIds: string[] = []
    selectedFiltersCount = 0;
    brandsSearchTerm: string = ''
    brandsSearchResults: string[] = []
    amenitiesSearchTerm: string =''
    amenitiesSearchResults: string[] = []
    amenitiesSearchResultsSections: string[] =[]
    minStay: number
    propertyStyles: string[]
    selectedPropertyStyles: string[] =[]
    selectedPropertyStylesStored: string [] = []
    allPropertyStylesActive = true
    brandExpandButtonLabels = ['show more', 'show less']
    minStayFilterButtonsData = [
        { value: 0, label: 'Any duration' },
        { value: 2, label: '2 nights' },
        { value: 3, label: '3 nights' },
        { value: 4, label: '4 nights' },
        { value: 5, label: '5 nights' },
        { value: 7, label: '7 nights' },
        { value: 10, label: '10 nights' },
        { value: 14, label: '14 nights' }
    ]

    @Output() $moreFiltersChange = new EventEmitter<MoreFilters>()
    @Output() $selectedFilterCountChanged = new EventEmitter<number>()

    @ViewChild('form') form: NgForm

    constructor(private _searchCriteriaBrandsGQL: SearchCriteriaBrandsGQL) {
        super()
    }

    ngAfterContentInit(): void {
        this.refresh()
    }

    private refresh() {
        this._subscriptions.push(this._searchCriteriaBrandsGQL
            .watch({}, {fetchPolicy: 'no-cache'})
            .valueChanges
            .subscribe(result => {
                if (!result.loading) {
                    this.brands = result.data.brands.sort((one, other) =>
                        one.name?.toLocaleLowerCase().localeCompare(other?.name?.toLowerCase())) as Array<Brand>
                    this.brandIds = this.brands.map(brand => brand.brandId)
                    this.propertyStyles = Object.values(PropertyStyle)
                    this.keyAmenityIds = amenitiesData.keyAmenities.map(amenity => amenity.amenityId)
                    // Older version of TS not happy with flat() so had to use 'any'
                    const otherAmenities: any = amenitiesData.otherAmenities.map(section => section.amenities.map(amenity => amenity))
                    this.otherAmenities = otherAmenities.flat(Infinity)
                    this.otherAmenityIds = this.otherAmenities.map(amenity => amenity.amenityId)
                    this.selectedBrandIds = this.moreFilters?.brandsCriterion?.brandIds || []
                    this.allBrandIdsActive = this.moreFilters?.brandsCriterion?.brandIds.length === 0
                    this.selectedAmenityIds = this.moreFilters?.amenitiesCriterion?.selectedAmenityIds || []
                    this.selectedPropertyStyles = this.moreFilters?.styleCriterion?.propertyStyles || []
                    this.allPropertyStylesActive = this.moreFilters?.styleCriterion?.propertyStyles.length === 0
                    this.brandExpandButtonLabels = [`${this.brands.length} more managing brands`, 'show less']
                    this.createFormGroup(this.moreFilters)
                    this.selectedFiltersCount = this.getSelectedFiltersCount()
                    this.$selectedFilterCountChanged.emit(this.selectedFiltersCount)
                }
            }))
    }

    brandIsSelected(brand: Brand) {
        return this.moreFilters?.brandsCriterion?.brandIds.some(brandId => brandId === brand.brandId)
    }

    amenityIsSelected(amenity: string) {
        return this.moreFilters?.amenitiesCriterion?.selectedAmenityIds.some(amenityId => amenityId === amenity)
    }

    propertyStyleIsSelected(style: string) {    
        return this.moreFilters?.styleCriterion?.propertyStyles.some(propertyStyle => propertyStyle === style)
    }

    toggleSelectAll(controlName: string) {
        const controlNameCap = capitalize(controlName)

        if (!this[`all${controlNameCap}Active`]) {
            // store selected filters and clear
            this[`selected${controlNameCap}Stored`] = this[`selected${controlNameCap}`].slice()
            this.clearFilters(controlName)
        } else {
            // reinstate previously selected filters
            this.reinstateStoredFilters(controlName)
            this[`selected${controlNameCap}Stored`].length = 0
        }

        this[`all${controlNameCap}Active`] = !this[`all${controlNameCap}Active`]
    }

    reinstateStoredFilters(controlName: string) {
        const controlNameCap = capitalize(controlName)
        const formArray = this.formGroup.get(controlName) as FormArray

        this[controlName].forEach((item: string, index: number): void  => {
            if (this[`selected${controlNameCap}Stored`].indexOf(item) > -1) {
                formArray.controls[index].setValue(true)
            }
        })
    }

    clearAllFilters() {
        this.brandsSearchTerm = ''
        this.formGroup.reset()
        this.form.resetForm()
        this.moreFilters = defaultMoreFilters()
        this.refresh()
    }

    clearFilters(controlName: string) {
        this.formGroup.controls[controlName].reset()
    }

    resetMinStayFilter() {
        this.formGroup.controls['minStay'].setValue(0)
    }

    getSelectedFiltersCount() {
        return this.selectedFiltersCount = this.selectedBrandIds.length + this.selectedAmenityIds.length + (this.minStay > 0 ? 1 : 0) + this.selectedPropertyStyles.length
    }

    searchBrands(target: EventTarget) {
        if (!(target instanceof HTMLInputElement)) { return }
        this.brandsSearchResults.length = 0;
        this.brandsSearchTerm = target.value.toLowerCase()

        this.brands.forEach(brand => {
            if (brand.name.toLowerCase().startsWith(this.brandsSearchTerm) && this.brandsSearchResults.indexOf(brand.name) === -1) {
                this.brandsSearchResults.push(brand.name)
            } 
        })
    }

    searchAmenities(target: EventTarget) {
        if (!(target instanceof HTMLInputElement)) { return }
        this.amenitiesSearchResults.length = 0
        this.amenitiesSearchTerm = target.value.toLowerCase()
        const allAmenities = [...amenitiesData.keyAmenities, ...this.otherAmenities]

        allAmenities.forEach(amenity => {
            const amenityName = amenity.name.toLowerCase()
            if ((amenityName.startsWith(this.amenitiesSearchTerm) || amenityName.includes(this.amenitiesSearchTerm)) && this.amenitiesSearchResults.indexOf(amenity.name) === -1) {
                this.amenitiesSearchResults.push(amenity.name)
            }
        })
    }

    updateListingCount(count: number) {
        this.listingCount = count
    }

    private createFormGroup(moreFilters: MoreFilters): void {
        this.formGroup = new FormGroup({
            brandIds: new FormArray([]),
            propertyStyles: new FormArray([]),
            minStay: new FormControl(moreFilters?.minimumStayNightsCriterion?.minimumStayNights || 0),
            keyAmenityIds: new FormArray([]),
            otherAmenityIds: new FormArray([])
        })

        this.brands.forEach((brand): void => {
            (this.formGroup.get('brandIds') as FormArray).push(new FormControl(this.brandIsSelected(brand)))
        })

        this.keyAmenityIds.forEach((keyAmenity: string): void => {
            (this.formGroup.get('keyAmenityIds') as FormArray).push(new FormControl(this.amenityIsSelected(keyAmenity)))
        })

        this.otherAmenityIds.forEach((otherAmenity: string): void => {
            (this.formGroup.get('otherAmenityIds') as FormArray).push(new FormControl(this.amenityIsSelected(otherAmenity)))
        })

        this.propertyStyles.forEach((style: string): void => {
            (this.formGroup.get('propertyStyles') as FormArray).push(new FormControl(this.propertyStyleIsSelected(style)))
        })

        this._subscriptions.push(this.formGroup
            .valueChanges
            .pipe(debounceTime(100))
            .subscribe((event) => {
                this.selectedBrandIds = this.brands.map((brand, index) => event.brandIds[index] ? brand.brandId : null ).filter(Boolean)
                this.allBrandIdsActive = this.selectedBrandIds.length ? false : true;
                this.minStay = event.minStay
                this.selectedKeyAmenityIds = this.keyAmenityIds.map((amenityId, index) => event.keyAmenityIds[index] ? amenityId : null).filter(Boolean)
                this.selectedOtherAmenityIds = this.otherAmenityIds.map((amenityId, index) => event.otherAmenityIds[index] ? amenityId : null).filter(Boolean)
                this.selectedAmenityIds = [...this.selectedKeyAmenityIds, ...this.selectedOtherAmenityIds]
                this.selectedPropertyStyles = this.propertyStyles.map((style, index) => event.propertyStyles[index] ? style : null).filter(Boolean)
                this.allPropertyStylesActive = this.selectedPropertyStyles.length ? false : true;
                this.selectedFiltersCount = this.getSelectedFiltersCount()
                this.notifyListeners()
            }))
    }

    private notifyListeners() {
        const moreFilters = {
            brandsCriterion: {
                brandIds: this.allBrandIdsActive ? [] : this.selectedBrandIds
            },
            amenitiesCriterion: {
                selectedAmenityIds: this.selectedAmenityIds
            },
            minimumStayNightsCriterion: {
                minimumStayNights: this.minStay
            },
            styleCriterion: {
                propertyStyles: this.allPropertyStylesActive ? [] : this.selectedPropertyStyles
            }
        } as MoreFilters

        console.log('Emitting updated MoreFilters', moreFilters)               
        this.$selectedFilterCountChanged.emit(this.selectedFiltersCount)
        this.$moreFiltersChange.emit(moreFilters)
        this.formGroup.markAsPristine()
    }
}
